import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Card, Select } from "antd";
import { EyeInvisibleOutlined, EyeOutlined } from "@ant-design/icons";
import { toast } from "react-hot-toast";
import Footer from "../components/Footer";
import Header from "../components/Header";
import { useTranslation } from "react-i18next";
import StandardButton from "../components/StandardButton";

const ALGORITHMS = ["SHA-1", "SHA-256", "SHA-512"];
const DIGIT_OPTIONS = [6, 7, 8];
const PERIOD_OPTIONS = [15, 30, 60];

const BASE32_ALPHABET = "ABCDEFGHIJKLMNOPQRSTUVWXYZ234567";

const cleanBase32 = (raw) =>
  (raw || "")
    .toUpperCase()
    .replace(/\s+/g, "")
    .replace(/=+$/g, "")
    .replace(/[^A-Z2-7]/g, "");

const isValidBase32 = (raw) => {
  const cleaned = cleanBase32(raw);
  return cleaned.length > 0 && /^[A-Z2-7]+$/.test(cleaned);
};

const base32ToBytes = (raw) => {
  const cleaned = cleanBase32(raw);
  const out = [];
  let buffer = 0;
  let bits = 0;
  for (let i = 0; i < cleaned.length; i += 1) {
    const idx = BASE32_ALPHABET.indexOf(cleaned[i]);
    if (idx < 0) throw new Error("invalid base32 character");
    buffer = (buffer << 5) | idx;
    bits += 5;
    if (bits >= 8) {
      bits -= 8;
      out.push((buffer >> bits) & 0xff);
    }
  }
  return new Uint8Array(out);
};

const counterBytes = (counter) => {
  const buf = new ArrayBuffer(8);
  const view = new DataView(buf);
  const high = Math.floor(counter / 0x100000000);
  const low = counter >>> 0;
  view.setUint32(0, high);
  view.setUint32(4, low);
  return buf;
};

const truncate = (hmac, digits) => {
  const offset = hmac[hmac.length - 1] & 0x0f;
  const binary =
    ((hmac[offset] & 0x7f) << 24) |
    ((hmac[offset + 1] & 0xff) << 16) |
    ((hmac[offset + 2] & 0xff) << 8) |
    (hmac[offset + 3] & 0xff);
  const mod = 10 ** digits;
  return String(binary % mod).padStart(digits, "0");
};

const computeTotp = async (secretBytes, counter, algorithm, digits) => {
  const key = await crypto.subtle.importKey(
    "raw",
    secretBytes,
    { name: "HMAC", hash: { name: algorithm } },
    false,
    ["sign"],
  );
  const sig = await crypto.subtle.sign(
    "HMAC",
    key,
    counterBytes(counter),
  );
  return truncate(new Uint8Array(sig), digits);
};

const formatCode = (code) => {
  if (!code) return "";
  const mid = Math.ceil(code.length / 2);
  return `${code.slice(0, mid)} ${code.slice(mid)}`;
};

const TotpGenerator = () => {
  const { t } = useTranslation();
  const [secret, setSecret] = useState("JBSWY3DPEHPK3PXP");
  const [algorithm, setAlgorithm] = useState("SHA-1");
  const [digits, setDigits] = useState(6);
  const [period, setPeriod] = useState(30);
  const [now, setNow] = useState(() => Date.now());
  const [current, setCurrent] = useState("");
  const [previous, setPrevious] = useState("");
  const [next, setNext] = useState("");
  const [error, setError] = useState("");
  const [secretVisible, setSecretVisible] = useState(false);
  const timerRef = useRef(null);

  const cleanedSecret = useMemo(() => cleanBase32(secret), [secret]);
  const secretIsValid = useMemo(
    () => isValidBase32(cleanedSecret),
    [cleanedSecret],
  );

  const counter = useMemo(
    () => Math.floor(now / 1000 / period),
    [now, period],
  );
  const elapsedInWindow = useMemo(
    () => Math.floor(now / 1000) % period,
    [now, period],
  );
  const remaining = period - elapsedInWindow;
  const progress = Math.max(0, Math.min(100, (remaining / period) * 100));

  const refreshCodes = useCallback(async () => {
    if (!secretIsValid) {
      setCurrent("");
      setPrevious("");
      setNext("");
      setError(cleanedSecret ? t("totp_error_invalid_secret") : "");
      return;
    }
    try {
      const bytes = base32ToBytes(cleanedSecret);
      if (bytes.length === 0) {
        throw new Error("empty secret");
      }
      const [prev, curr, nxt] = await Promise.all([
        computeTotp(bytes, Math.max(0, counter - 1), algorithm, digits),
        computeTotp(bytes, counter, algorithm, digits),
        computeTotp(bytes, counter + 1, algorithm, digits),
      ]);
      setPrevious(prev);
      setCurrent(curr);
      setNext(nxt);
      setError("");
    } catch {
      setCurrent("");
      setPrevious("");
      setNext("");
      setError(t("totp_error_invalid_secret"));
    }
  }, [cleanedSecret, secretIsValid, counter, algorithm, digits, t]);

  useEffect(() => {
    refreshCodes();
  }, [refreshCodes]);

  useEffect(() => {
    timerRef.current = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timerRef.current);
  }, []);

  const copyCode = async (code) => {
    if (!code) {
      toast.error(t("totp_nothing_to_copy"));
      return;
    }
    try {
      await navigator.clipboard.writeText(code);
      toast.success(t("copy_success"));
    } catch {
      toast.error(t("failed_to_copy"));
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-indigo-50 to-white p-0 text-gray-800">
      <Header />
      <div className="max-w-5xl mx-auto p-4">
        <Card className="shadow-lg rounded-xl">
          <div className="p-4 flex flex-col gap-4">
            <h1 className="text-2xl font-bold text-indigo-700">
              {t("totp_title")}
            </h1>
            <p className="text-gray-600">{t("totp_intro")}</p>

            <div className="flex flex-col gap-1">
              <label
                htmlFor="totp-secret-input"
                className="text-sm font-medium text-gray-700"
              >
                {t("totp_secret_label")}
              </label>
              <div className="relative">
                <input
                  id="totp-secret-input"
                  type={secretVisible ? "text" : "password"}
                  value={secret}
                  onChange={(e) => setSecret(e.target.value)}
                  placeholder={t("totp_secret_placeholder")}
                  spellCheck={false}
                  autoComplete="off"
                  className="w-full p-2 pr-10 font-mono text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-400 focus:outline-none"
                />
                <button
                  type="button"
                  onClick={() => setSecretVisible((v) => !v)}
                  aria-label={
                    secretVisible
                      ? t("totp_hide_secret_aria")
                      : t("totp_show_secret_aria")
                  }
                  aria-pressed={secretVisible}
                  title={
                    secretVisible
                      ? t("totp_hide_secret_aria")
                      : t("totp_show_secret_aria")
                  }
                  className="absolute inset-y-0 right-0 flex items-center justify-center px-2 text-indigo-700 hover:bg-indigo-50 rounded-r-md focus:outline-none focus:ring-2 focus:ring-indigo-400"
                >
                  {secretVisible ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                </button>
              </div>
              <span className="text-xs text-gray-500">
                {t("totp_secret_hint")}
              </span>
            </div>

            <div className="flex flex-wrap items-end gap-4">
              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-gray-700">
                  {t("totp_algorithm_label")}
                </label>
                <Select
                  value={algorithm}
                  onChange={setAlgorithm}
                  style={{ width: 160 }}
                  options={ALGORITHMS.map((a) => ({ value: a, label: a }))}
                />
              </div>

              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-gray-700">
                  {t("totp_digits_label")}
                </label>
                <Select
                  value={digits}
                  onChange={setDigits}
                  style={{ width: 120 }}
                  options={DIGIT_OPTIONS.map((d) => ({
                    value: d,
                    label: String(d),
                  }))}
                />
              </div>

              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-gray-700">
                  {t("totp_period_label")}
                </label>
                <Select
                  value={period}
                  onChange={setPeriod}
                  style={{ width: 140 }}
                  options={PERIOD_OPTIONS.map((p) => ({
                    value: p,
                    label: t("totp_period_seconds", { seconds: p }),
                  }))}
                />
              </div>
            </div>

            {error && (
              <div className="px-3 py-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md">
                {error}
              </div>
            )}

            <div className="flex flex-col gap-3 p-4 bg-gray-50 border border-gray-200 rounded-md">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700">
                  {t("totp_current_label")}
                </span>
                <StandardButton
                  onClick={() => copyCode(current)}
                  type="primary"
                  size="small"
                  disabled={!current}
                >
                  {t("copy_button")}
                </StandardButton>
              </div>
              <div className="text-4xl font-mono font-bold tracking-widest text-indigo-700 select-all">
                {current ? formatCode(current) : "— —"}
              </div>
              <div className="flex items-center gap-3">
                <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-indigo-500 transition-all duration-1000 ease-linear"
                    style={{ width: `${progress}%` }}
                  />
                </div>
                <span className="text-xs text-gray-600 w-28 text-right">
                  {t("totp_refresh_in", { seconds: remaining })}
                </span>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="flex flex-col gap-1 p-3 bg-white border border-gray-200 rounded-md">
                <span className="text-xs text-gray-500">
                  {t("totp_previous_label")}
                </span>
                <span className="text-lg font-mono text-gray-700">
                  {previous ? formatCode(previous) : "—"}
                </span>
              </div>
              <div className="flex flex-col gap-1 p-3 bg-white border border-gray-200 rounded-md">
                <span className="text-xs text-gray-500">
                  {t("totp_next_label")}
                </span>
                <span className="text-lg font-mono text-gray-700">
                  {next ? formatCode(next) : "—"}
                </span>
              </div>
            </div>

            <div className="text-xs text-gray-500 leading-relaxed">
              <p>{t("totp_note_compat")}</p>
              <p>{t("totp_note_clock")}</p>
              <p>{t("totp_note_privacy")}</p>
            </div>
          </div>
        </Card>
      </div>
      <Footer />
    </div>
  );
};

export default TotpGenerator;
